import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormControl, FormGroup, FormRecord, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, debounceTime, distinctUntilChanged, map, of, shareReplay, switchMap, take } from 'rxjs';
import { Attendee, CheckoutData, Country, Event, Fee, InvoicingData, PromoCode, RegistrationData } from '../../../domain/registrator';
import { promoCodeValidatorFactory } from '../../../domain/utils';
import { BlankOrNAPipe } from '../../../pipes/blank-or-na.pipe';
import { CaptchaService } from '../../../services/captcha.service';
import { CheckoutService } from '../../../services/checkout.service';
import { CountryService } from '../../../services/country.service';
import { PromocodeService } from '../../../services/promocode.service';
import { ConfirmCheckoutDialogComponent } from '../confirm-checkout-dialog/confirm-checkout-dialog.component';
import { OptionGroupComponent } from '../../misc/option-group/option-group.component';
import { MessageService } from '../../../services/message.service';
import { RegistrationStore } from '../../../guards/registration-store';

export interface RegistrationForm {
  organisation: FormControl<string>;
  selectedFee: FormControl<Fee | null>;
  firstAttendee: FormGroup<AttendeeForm>;
  additionalAttendees: FormArray<FormGroup<AttendeeForm>>;
}

export interface InvoicingForm {
  organisation: FormControl<string>;
  contactName: FormControl<string>;
  contactEmail: FormControl<string>;
  address1: FormControl<string>;
  address2: FormControl<string | null>;
  zipCode: FormControl<string>;
  city: FormControl<string>;
  country: FormControl<string>;
  noVAT: FormControl<boolean>;
  vatNumber: FormControl<string | null>;
  poNumber: FormControl<string | null>;
}

export interface AttendeeForm {
  jobTitle: FormControl<string>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
  preferences: FormGroup<PreferencesForm>;
  corresponding: FormControl<boolean>,
};

export interface PreferencesForm {
  [groupId: number]: FormControl<number[]>;
}

@Component({
  selector: 'app-registration',
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatOptionModule,
    MatRadioModule,
    MatSelectModule,
    MatStepperModule,
    MatTableModule,
    ReactiveFormsModule,
    BlankOrNAPipe,
    MatProgressSpinnerModule,
    OptionGroupComponent
  ],
  templateUrl: './registration.component.html',
  styleUrl: './registration.component.scss'
})
export class RegistrationComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly promocodeService = inject(PromocodeService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly messageService = inject(MessageService);
  private readonly checkoutService = inject(CheckoutService);
  private readonly countryService = inject(CountryService);
  private readonly registrationStore = inject(RegistrationStore);
  private readonly captchaService = inject(CaptchaService);
  event = this.route.parent!.snapshot.data['event']; // added by resolver

  loading = signal<boolean>(false);

  feeSelectionModel: SelectionModel<Fee> = new SelectionModel<Fee>(false, []);
  selectedFee: Fee | null = null;
  countries: Country[] = [];
  attendeesColumnsToDisplay = ['firstName', 'lastName', 'jobTitle', 'email'];
  captchaToken: string | null = null;
  // honeypot: real users never fill this hidden field
  hpField = '';
  formLoadedAt = 0;


  readonly registrationFormGroup = this.formBuilder.group<RegistrationForm>({
    organisation: this.formBuilder.control('', { validators: Validators.required, nonNullable: true }),
    selectedFee: this.formBuilder.control(null),
    firstAttendee: this.buildAttendeeFormGroup(true),
    additionalAttendees: this.formBuilder.array<FormGroup<AttendeeForm>>([])
  }, {
    updateOn: 'blur'
  });

  // readonly registrationFormGroup = this.formBuilder.group({
  //   organisation: ['', Validators.required],
  //   selectedFee: [null as Fee | null],
  //   firstAttendee: this.buildAttendeeFormGroup(true),
  //   additionalAttendees: this.formBuilder.array<FormGroup<AttendeeForm>>([]),
  // });

  readonly invoicingFormGroup = this.formBuilder.group<InvoicingForm>({
    organisation: this.formBuilder.control('', { validators: Validators.required, nonNullable: true }),
    contactName: this.formBuilder.control('', { validators: Validators.required, nonNullable: true }),
    contactEmail: this.formBuilder.control('', { validators: [Validators.required, Validators.email], nonNullable: true }),
    address1: this.formBuilder.control('', { validators: Validators.required, nonNullable: true }),
    address2: this.formBuilder.control(''),
    zipCode: this.formBuilder.control('', { validators: Validators.required, nonNullable: true }),
    city: this.formBuilder.control('', { validators: Validators.required, nonNullable: true }),
    country: this.formBuilder.control('', { validators: Validators.required, nonNullable: true }),
    noVAT: this.formBuilder.control(false, { nonNullable: true }),
    vatNumber: this.formBuilder.control(''),
    poNumber: this.formBuilder.control(''),
    //   organisation: ['', Validators.required],
    // contactName: ['', Validators.required],
    // contactEmail: ['', [Validators.required, Validators.email]],
    // address1: ['', Validators.required],
    // address2: [''],
    // zipCode: ['', Validators.required],
    // city: ['', Validators.required],
    // country: ['', Validators.required],
    // noVAT: [false],
    // vatNumber: [''],
    // poNumber: [''],
  });

  // Form control as a field initializer (no early deref)
  promoCodeControl = new FormControl('', {
    updateOn: 'blur',
    asyncValidators: [promoCodeValidatorFactory(this.promocodeService, this.event)]
  });

  // Observable as a field initializer; deref inside the pipeline
  promoCode$ = this.promoCodeControl.valueChanges.pipe(
    debounceTime(300),
    distinctUntilChanged(),
    switchMap(code => {
      const ev = this.event; // safe: read when subscribed
      if (!code || !ev) return of(null);
      return this.promocodeService.getEventPromocode(ev.shortName, code).pipe(
        catchError(() => of(null))
      );
    }),
    shareReplay(1)
  );
  promoCode = toSignal(this.promoCode$, { initialValue: null });

  registrationValue = toSignal(this.registrationFormGroup.valueChanges.pipe(map(() => this.registrationFormGroup.getRawValue())),
    { initialValue: this.registrationFormGroup.getRawValue() }
  );
  invoicingValue = toSignal(this.invoicingFormGroup.valueChanges.pipe(map(() => this.invoicingFormGroup.getRawValue())),
    { initialValue: this.invoicingFormGroup.getRawValue() }
  );
  registrationStatus = toSignal(this.registrationFormGroup.statusChanges, { initialValue: this.registrationFormGroup.status });
  invoicingStatus = toSignal(this.invoicingFormGroup.statusChanges, { initialValue: this.invoicingFormGroup.status });

  checkoutData = computed(() => {
    const regStatus = this.registrationStatus();
    const invStatus = this.invoicingStatus();
    const promoCode = this.promoCode();
    const registrationData: RegistrationData = {
      ...this.registrationValue(),
      selectedFee: this.selectedFee!,
      promoCode: (promoCode ? promoCode?.code : null)
    };
    const invoicingData: InvoicingData = {
      ...this.invoicingValue(),
      vatNumber: this.invoicingValue().noVAT ? null : this.invoicingValue().vatNumber,
      poNumber: this.invoicingValue().poNumber || null,
    };
    return this.computeCheckoutData(regStatus, invStatus, registrationData, invoicingData, promoCode);
  });

  constructor() {
  }

  ngOnInit(): void {
    this.formLoadedAt = Date.now();

    console.log('promoCodeControl:', this.promoCodeControl);
    console.log('event:', this.event);
    console.log('promocodeService:', this.promocodeService);

    if (this.event.fees.length > 1) {
      this.registrationFormGroup.controls['selectedFee'].setValidators(Validators.required);
    } else {
      if (this.event.fees.length == 1) {
        this.selectedFee = this.event.fees[0];
      }
      this.registrationFormGroup.controls['selectedFee'].clearValidators();
    }
    this.countryService.countries.subscribe((x) => {
      this.countries = x;
    });
    this.invoicingFormGroup.get('noVAT')!.valueChanges.subscribe(checked => {
      if (checked) {
        this.invoicingFormGroup.get('vatNumber')!.disable();
        this.invoicingFormGroup.get('vatNumber')!.setValue('N/A');
      } else {
        this.invoicingFormGroup.get('vatNumber')!.enable();
      }
    })
  }

  get earlyBirdDeadline() {
    return this.event.earlyBirdDeadline;
  }

  get baseFee() {
    if (this.selectedFee)
      return (this.isEarlyBirdActive ? this.selectedFee.earlyBirdPrice! : this.selectedFee.generalPrice)
    else
      return 0;
  }

  get fees() {
    return this.event.fees;
  }

  get isEarlyBirdActive() {
    return this.event.isEarlyBirdActive();
  }

  get additionalAttendees() {
    return this.registrationFormGroup.controls["additionalAttendees"] as FormArray<FormGroup<AttendeeForm>>;
  }

  get feeColumnsToDisplay() {
    if (this.isEarlyBirdActive) {
      return ['select', 'target', 'earlyBirdPrice', 'generalPrice']
    } else {
      return ['select', 'target', 'generalPrice']
    }
  }

  get allAttendees(): Attendee[] {
    let allAttendees: Attendee[] = [];

    const firstAttendeeGroup = this.registrationFormGroup.get('firstAttendee') as FormGroup;
    const firstAttendee: Attendee = firstAttendeeGroup.value;
    const attendeesArray = this.registrationFormGroup.get('additionalAttendees') as FormArray;
    const additionalAttendees: Attendee[] = attendeesArray.value;

    if ((firstAttendeeGroup.valid) && (attendeesArray.valid)) {
      allAttendees = [firstAttendee, ...additionalAttendees];
    }
    return allAttendees;
  }

  // getAdditionalAttendeeFormGroup(): FormGroup {
  //   return this.formBuilder.group({
  //     jobTitle: ['', Validators.required],
  //     firstName: ['', Validators.required],
  //     lastName: ['', Validators.required],
  //     email: ['', [Validators.required, Validators.email]],
  //   });
  // }
  // private getAdditionalAttendeeFormGroup(corresponding: boolean): FormGroup<{
  //   jobTitle: FormControl<string>;
  //   firstName: FormControl<string>;
  //   lastName: FormControl<string>;
  //   email: FormControl<string>;
  //   corresponding: FormControl<boolean>
  // }> {
  //   return this.formBuilder.group({
  //     jobTitle: this.formBuilder.control('', { validators: [Validators.required] }),
  //     firstName: this.formBuilder.control('', { validators: [Validators.required] }),
  //     lastName: this.formBuilder.control('', { validators: [Validators.required] }),
  //     email: this.formBuilder.control('', { validators: [Validators.required, Validators.email] }),
  //     preferences: this.formBuilder.group(preferenceControls as PreferencesForm, { updateOn: 'blur' }),
  //     corresponding: this.formBuilder.control(corresponding),
  //   });
  // }

  private buildAttendeeFormGroup(corresponding: boolean): FormGroup<AttendeeForm> {
    const preferenceControls: PreferencesForm = {};

    for (const group of this.event.optionGroups) {
      preferenceControls[group.id] = this.formBuilder.control<number[]>([], {
        nonNullable: true
      });
    }

    const fg = this.formBuilder.group<AttendeeForm>({
      jobTitle: this.formBuilder.control('', { validators: [Validators.required], nonNullable: true }),
      firstName: this.formBuilder.control('', { validators: [Validators.required], nonNullable: true }),
      lastName: this.formBuilder.control('', { validators: [Validators.required], nonNullable: true }),
      email: this.formBuilder.control('', { validators: [Validators.required, Validators.email], nonNullable: true }),
      preferences: this.formBuilder.group(preferenceControls as PreferencesForm, { updateOn: 'blur' }),
      corresponding: this.formBuilder.control(corresponding, { nonNullable: true }),
    }, {
      updateOn: 'blur'
    });
    return fg;
  }

  addAttendee() {
    this.additionalAttendees.push(this.buildAttendeeFormGroup(false));
  }

  deleteAttendee(i: number) {
    console.log("deleting " + i);
    this.additionalAttendees.removeAt(i);
  }

  isFeeSelected(fee: Fee): boolean {
    return this.feeSelectionModel.isSelected(fee);
  }

  toggleFeeSelection(fee: Fee): void {
    this.feeSelectionModel.toggle(fee);
    this.registrationFormGroup.controls['selectedFee'].setValue(this.feeSelectionModel.hasValue() ? fee : null);
    this.selectedFee = fee;
  }

  checkVAT() {
    let vatNumber = this.invoicingFormGroup.get('vatNumber')!.value;
    //this.viesService.checkVATNumber(vatNumber).subscribe((response) => { console.debug(response) });
  }

  computeCheckoutData(regStatus: string, invStatus: string, registrationData: RegistrationData,
    invoicingData: InvoicingData, promoCode: PromoCode | null) {
    console.log('recalculating checkoutData')
    if (regStatus !== 'VALID' || invStatus !== 'VALID') {
      return null;
    }

    const totalAttendees = this.allAttendees.length
    const baseRate = this.baseFee;
    let discountRate = baseRate;
    const freeTickets = (this.event.oneFreeFor == 0 ? 0 : Math.trunc(totalAttendees / this.event.oneFreeFor));
    const payingTickets = totalAttendees - freeTickets;
    let fullPriceTickets = payingTickets;
    let discountTickets = 0;
    let totalAmount = 0;
    if (promoCode) {
      if (promoCode.maxUses == 0) {
        discountTickets = payingTickets;
        fullPriceTickets = 0;
      } else {
        const leftUses = Math.max(0, (promoCode.maxUses - promoCode.uses));
        discountTickets = Math.min(leftUses, payingTickets);
        fullPriceTickets = payingTickets - discountTickets;
      }
      const discountType = promoCode.discountType;
      let discount = 0;
      switch (discountType) {
        case 'PERCENT':
          // Has to be rounded to 2 decimals
          discount = (Math.round(baseRate * promoCode.discountValue) / 100);
          break
        case 'FIXED_AMOUNT':
          discount = promoCode.discountValue;
          break;
        default:
          console.error('invalid discount type: ', discountType)
          break
      }
      discountRate = baseRate - discount;
      totalAmount = (fullPriceTickets * baseRate) + (discountTickets * discountRate)
    } else {
      totalAmount = (fullPriceTickets * baseRate)
    }

    const checkoutData: CheckoutData = {
      eventShortName: this.event.shortName,
      registrationData: registrationData,
      invoicingData: invoicingData,
      totalTickets: totalAttendees,
      payingTickets: payingTickets,
      freeTickets: freeTickets,
      fullRateTickets: fullPriceTickets,
      discountRateTickets: discountTickets,
      fullRate: Math.round(baseRate * 100) / 100,
      discountRate: Math.round(discountRate * 100) / 100,
      totalAmount: Math.round(totalAmount * 100) / 100,
      paymentMode: ('INVOICE'),
      token: this.captchaToken,
      hpField: this.hpField,
      formLoadedAt: this.formLoadedAt,
      successUrl: window.location.origin + `/${(this.event ? this.event.shortName : '')}/registration/success`,
      cancelUrl: window.location.origin + `/${(this.event ? this.event.shortName : '')}/registration/cancel`,
      pendingUrl: window.location.origin + `/${(this.event ? this.event.shortName : '')}/registration/pending`
    };
    return checkoutData;
  }

  checkout() {
    const dialogConfig = {
      width: '800px',
      minWidth: '800px',
      // minHeight: '7',
      // maxHeight: '90vh',
      data: this.checkoutData()
    }

    const dialogRef = this.dialog.open(ConfirmCheckoutDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.submitCheckout();
      }
    });
  }

  async submitCheckout() {
    console.debug("submitting checkout", this.checkoutData());
    this.loading.set(true);
    // solve the proof-of-work captcha at submit time, so the token is fresh
    try {
      this.captchaToken = await this.captchaService.execute('checkout');
    } catch (e) {
      this.loading.set(false);
      console.error('captcha failed', e);
      this.messageService.showMessage('Captcha verification failed; please try again');
      return;
    }

    // build the payload explicitly: checkoutData is a cached computed, so setting
    // captchaToken/hpField on the component would not be reflected in it.
    // hpField is cleared here because real users run JS; only non-JS bots that
    // filled the hidden input leave it non-empty (browser autofill is cleared too).
    const data: CheckoutData = { ...this.checkoutData()!, token: this.captchaToken, hpField: '' };

    this.checkoutService.checkout(data).pipe(take(1)).subscribe({
      next: response => {
        this.loading.set(false);
        this.registrationStore.markCompleted();
        const url = response.body?.paymentUrl;

        if (url)
          this.router.navigate([url]);
        else
          this.router.navigate(['confirm'], {
            relativeTo: this.route
          });
      },
      error: err => {
        this.loading.set(false);
        // const message = err.error?.title || 'Unexpected error occurred';
        const message = 'An error occurred while processing the registration';
        console.error(err.status, message, err.error.detail);
        this.messageService.showMessage(message);
      }
    })
  }
}
